import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import Checkbox from '@material-ui/core/Checkbox';

import Button from '../../../../shared/_TBComponents/Button';
import TBTooltip from '../../../../shared/_TBComponents/Tooltip';
import {
  setExerFiltersPerformance,
  setIsExerFiltersPerformanceShowing,
} from '../../ducks/sportCoachDashboardActions';
import { BoxShadow } from '../../../../shared/GlobalStyles';
import { dashboardTracker } from '../../../../shared/utils/amplitudeHelper';

const Badge = styled('span')`
  background-color: #444444;
  border-radius: 100px;
  color: #FFFFFF;
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 11px;
  font-weight: bold;
  justify-content: center;
  position: absolute;
  right: -7px;
  top: -7px;
  width: 16px;
`;

const FilterAndButtonContainer = styled('div')`
  margin-top: 8px;
  position: relative;
`;

const FilterBottom = styled('div')`
  display: flex;
  justify-content: space-around;
  margin-top: 16px;
`;

const FilterContainer = styled('div')`
  background-color: #FFFFFF;
  border-radius: 4px;
  box-shadow: ${BoxShadow};
  display: flex;
  flex-direction: column;
  max-height: 420px;
  padding: 16px;
  position: absolute;
  top: 38px;
  width: 250px;
  z-index: 1;
`;

const FilterHeader = styled('div')`
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 20px;
  font-weight: 700;
  justify-content: space-between;
`;

const FilterOption = styled('div')`
  align-items: center;
  display: flex;
  margin-left: -10px;

  h3 {
    font-weight: normal;
  }
`;

const Filters = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: bold;
  overflow-y: auto;
`;

const MaxText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 100;
`;

const OptionText = styled('div')`
  color: ${(props) => (props.isDisabled ? 'rgba(0, 0, 0, 0.26)' : '#444444')};
  font-family: 'Nunito Sans';
  font-weight: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExerciseFilter = () => {
  const {
    exerciseFiltersPerformance,
    exercises,
  } = useSelector((state) => state.sportCoachDashboard.data);
  const { isExerciseFiltersPerformanceShowing } = useSelector(
    (state) => state.sportCoachDashboard.ui,
  );

  const dispatch = useDispatch();

  const hasFilters = exerciseFiltersPerformance.length > 0;

  const sortedExercises = Array.isArray(exercises) && exercises.length
    ? [...exercises].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  // Can only show up to 3 exercises with the same unit measurements
  const handleChange = (exer) => {
    // Amplitude tracking
    dashboardTracker(
      'Sport Coach Dashboard',
      'Performance Overview - Filter',
      `Exercise filter clicked - ${exer.name}`,
    );
    if (exerciseFiltersPerformance.includes(exer)) {
      const newExercises = exerciseFiltersPerformance.filter((exercise) => exercise !== exer);
      dispatch(setExerFiltersPerformance(newExercises));
    } else {
      // If there are already 3 filters, remove the first one
      // Can only filter a max of 3 exercises
      const newExercises = [...exerciseFiltersPerformance, exer];
      if (newExercises.length > 3) {
        newExercises.shift();
      }
      dispatch(setExerFiltersPerformance(newExercises));
    }
  };

  const handleClear = () => {
    // Amplitude tracking
    dashboardTracker('Sport Coach Dashboard', 'Performance Overview - Filter', 'Filters cleared');
    dispatch(setExerFiltersPerformance([]));
  };

  const handleClose = () => {
    // Amplitude tracking
    dashboardTracker('Sport Coach Dashboard', 'Performance Overview - Filter', 'Filter closed');
    dispatch(setIsExerFiltersPerformanceShowing(false));
  };

  const handleFilter = () => {
    // Amplitude tracking
    dashboardTracker('Sport Coach Dashboard', 'Performance Overview - Filter', 'Filter clicked');
    dispatch(setIsExerFiltersPerformanceShowing(!isExerciseFiltersPerformanceShowing));
  };

  // Should only be able to select exercises that can be graphed together
  const isExerciseDisabled = (option) => hasFilters
    && exerciseFiltersPerformance[0].unitAbbr !== option.unitAbbr;

  const renderExerciseOption = (option) => {
    const handleOptionChange = () => {
      handleChange(option);
    };

    const isDisabled = isExerciseDisabled(option);

    return (
      <FilterOption key={option.id}>
        <Checkbox
          checked={exerciseFiltersPerformance.includes(option)}
          color='default'
          disabled={isDisabled}
          onChange={handleOptionChange}
        />
        {option.name.length > 20 ? (
          <TBTooltip
            id={option.name}
            displayText={option.name}
            hoverText={option.name}
            tooltipStyles={{
              backgroundColor: '#FFFFFF',
              color: isDisabled ? 'rgba(0, 0, 0, 0.26)' : '#444444',
              fontSize: '16px',
            }}
          />
        ) : (
          <OptionText isDisabled={isDisabled}>
            {option.name}
          </OptionText>
        )}
      </FilterOption>
    );
  };

  const renderFilterModal = () => {
    if (!isExerciseFiltersPerformanceShowing) return null;

    return (
      <FilterContainer>
        <FilterHeader>
          Exercises
          <Button
            aria-label='Close exercise filter'
            disabled={!exerciseFiltersPerformance?.length}
            icon='remove'
            iconColor='#9E9E9E'
            iconOnly
            noBorder
            onClick={handleClose}
          />
        </FilterHeader>
        <MaxText>(Max. 3 Selections)</MaxText>
        <Filters>
          {sortedExercises.map(renderExerciseOption)}
        </Filters>
        <FilterBottom>
          <Button
            name='Clear'
            onClick={handleClear}
            rounded
            upperCase
          />
          <Button
            name='Done'
            bgColor='#444444'
            txtColor='#FFFFFF'
            disabled={!exerciseFiltersPerformance?.length}
            onClick={handleClose}
            rounded
            upperCase
          />
        </FilterBottom>
      </FilterContainer>
    );
  };

  return (
    <FilterAndButtonContainer>
      <Button
        name='Filter'
        icon='filter'
        onClick={handleFilter}
        rounded
        upperCase
      />
      {exerciseFiltersPerformance?.length > 0 && (
        <Badge>{exerciseFiltersPerformance.length}</Badge>
      )}
      {renderFilterModal()}
    </FilterAndButtonContainer>
  );
};

export default ExerciseFilter;
